// components/ProductSelection/ProductDetailsModal.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  XIcon,
  PhotographIcon,
  PlayIcon,
  PauseIcon,
  ScaleIcon,
  SparklesIcon,
  TagIcon,
  ShieldCheckIcon,
  OfficeBuildingIcon,
  CubeIcon,
  CheckCircleIcon,
  VideoCameraIcon,
  LocationMarkerIcon,
  CalendarIcon,
  UserIcon,
} from "@heroicons/react/outline";
import Badge from "../../common/Badge";
import Button from "../../common/Button";
import MediaLightbox from "./MediaLightbox";

const ProductDetailsModal = ({
  product,
  isOpen,
  onClose,
  onSelect,
  isSelected,
  adminView = false,
}) => {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState(
    adminView ? "details" : "specifications"
  ); // "details" | "specifications" | "availability"
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedMediaIndex(0);
      setIsVideoPlaying(false);
      setActiveTab(adminView ? "details" : "specifications");
    }
  }, [isOpen]);

  useEffect(() => {
    // Pause video when changing media
    if (videoRef.current) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  }, [selectedMediaIndex]);

  // Move useMemo hooks before early return to comply with Rules of Hooks
  const stockStatus = React.useMemo(() => {
    if (!product) return { label: "Out of Stock", variant: "danger" };
    const stock = product.stock?.availableQuantity || 0;
    if (stock === 0) return { label: "Out of Stock", variant: "danger" };
    if (stock <= (product.stock?.minStockLevel || 1)) {
      return { label: "Low Stock", variant: "warning" };
    }
    return { label: "In Stock", variant: "success" };
  }, [product]);

  const primaryPricing = React.useMemo(() => {
    if (!product?.pricing?.branches) return null;
    const branches = Object.entries(product.pricing.branches);
    const availableBranch = branches.find(([_, data]) => data.available);
    return availableBranch
      ? { branch: availableBranch[0], data: availableBranch[1] }
      : null;
  }, [product]);

  // Get all pricing branches
  const allPricingBranches = React.useMemo(() => {
    if (!product?.pricing?.branches) return [];
    return Object.entries(product.pricing.branches)
      .filter(([_, data]) => data.available)
      .map(([branch, data]) => ({ branch, data }));
  }, [product]);

  // Early return after all hooks
  if (!isOpen || !product) return null;

  const media = product.media || [];
  const selectedMedia = media[selectedMediaIndex] || media[0];
  const isVideo = selectedMedia?.type === "video";

  // Count media types
  const imageCount = media.filter((m) => m.type === "image").length;
  const videoCount = media.filter((m) => m.type === "video").length;

  const handleMediaClick = (index) => {
    setSelectedMediaIndex(index);
    setIsVideoPlaying(false);
  };

  const handleMediaZoom = () => {
    if (!isVideo) {
      setShowLightbox(true);
    }
  };

  const toggleVideoPlay = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative h-full flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-gray-900 truncate">
                  {product.name}
                </h2>
                <div className="flex items-center gap-3 mt-2">
                  <p className="text-sm text-gray-500">
                    SKU: {product.sku} • ID: {product.productId}
                  </p>
                  {product.createdAt && (
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" />
                      Added {new Date(product.createdAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4">
                {onSelect && (
                  <Button
                    onClick={() => onSelect(product)}
                    variant={isSelected ? "primary" : "secondary"}
                    size="md"
                  >
                    {isSelected ? (
                      <>
                        <CheckCircleIcon className="w-5 h-5 mr-2" />
                        Selected
                      </>
                    ) : (
                      "Select Product"
                    )}
                  </Button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                {/* Left Column - Media */}
                <div className="space-y-4">
                  {/* Main Media Display */}
                  <div
                    className={`relative bg-gray-100 rounded-xl overflow-hidden group ${
                      !isVideo ? "cursor-zoom-in" : ""
                    }`}
                    onClick={!isVideo ? handleMediaZoom : undefined}
                  >
                    {selectedMedia ? (
                      <>
                        {isVideo ? (
                          <div className="relative">
                            <video
                              ref={videoRef}
                              src={selectedMedia.url}
                              className="w-full h-[500px] object-contain bg-black"
                              controls
                              playsInline
                              onPlay={() => setIsVideoPlaying(true)}
                              onPause={() => setIsVideoPlaying(false)}
                            />
                            {!isVideoPlaying && (
                              <button
                                onClick={toggleVideoPlay}
                                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                              >
                                <div className="bg-white/90 rounded-full p-6 group-hover:scale-110 transition-transform">
                                  <PlayIcon className="w-16 h-16 text-gray-900" />
                                </div>
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <img
                              src={selectedMedia.url}
                              alt={selectedMedia.altText || product.name}
                              className="w-full h-[500px] object-contain group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2">
                                <PhotographIcon className="w-6 h-6 text-gray-700 inline mr-2" />
                                <span className="text-sm font-medium text-gray-700">
                                  Click to zoom
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-[500px] flex flex-col items-center justify-center">
                        <CubeIcon className="w-24 h-24 text-gray-400 mb-4" />
                        <p className="text-gray-500">No media available</p>
                      </div>
                    )}
                  </div>

                  {/* Media Thumbnails */}
                  {media.length > 1 && (
                    <div className="grid grid-cols-6 gap-2">
                      {media.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleMediaClick(index)}
                          className={`relative aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 transition-all ${
                            index === selectedMediaIndex
                              ? "border-primary scale-105 ring-2 ring-primary ring-offset-2"
                              : "border-transparent hover:border-gray-300"
                          }`}
                        >
                          {item.type === "image" ? (
                            <img
                              src={item.url}
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <>
                              <video
                                src={item.url}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                <PlayIcon className="w-6 h-6 text-white" />
                              </div>
                              <div className="absolute top-1 right-1 bg-black/70 rounded px-1 py-0.5">
                                <VideoCameraIcon className="w-3 h-3 text-white" />
                              </div>
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Media Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {imageCount > 0 && (
                        <Badge variant="secondary" size="sm">
                          <PhotographIcon className="w-3 h-3 mr-1" />
                          {imageCount} Photo{imageCount !== 1 ? "s" : ""}
                        </Badge>
                      )}
                      {videoCount > 0 && (
                        <Badge variant="info" size="sm">
                          <VideoCameraIcon className="w-3 h-3 mr-1" />
                          {videoCount} Video{videoCount !== 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>
                    {media.length > 0 && (
                      <p className="text-xs text-gray-500">
                        {selectedMediaIndex + 1} of {media.length}
                      </p>
                    )}
                  </div>

                  {/* Product Badges */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">
                      <SparklesIcon className="w-3 h-3 mr-1" />
                      {product.type}
                    </Badge>
                    {product.subType && (
                      <Badge variant="secondary">
                        {product.subType.replace("_", " ")}
                      </Badge>
                    )}
                    <Badge variant={stockStatus.variant}>
                      {stockStatus.label}
                    </Badge>
                    {product.hallmark?.isHallmarked && (
                      <Badge variant="warning">
                        <ShieldCheckIcon className="w-3 h-3 mr-1" />
                        Hallmarked
                      </Badge>
                    )}
                    {product.isFeatured && (
                      <Badge variant="success">Featured</Badge>
                    )}
                    {product.isNewArrival && (
                      <Badge variant="info">New Arrival</Badge>
                    )}
                    {product.isBestseller && (
                      <Badge variant="primary">Bestseller</Badge>
                    )}
                  </div>
                </div>

                {/* Right Column - Details */}
                <div className="space-y-6">
                  {/* Pricing */}
                  {primaryPricing && (
                    <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-primary-700 font-medium">
                          Price at {primaryPricing.branch}
                        </p>
                        {allPricingBranches.length > 1 && (
                          <Badge variant="info" size="sm">
                            +{allPricingBranches.length - 1} more branch
                            {allPricingBranches.length > 2 ? "es" : ""}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-baseline gap-2 mb-4">
                        <span className="text-4xl font-bold text-primary">
                          ₹
                          {primaryPricing.data.breakdown?.finalAmount?.toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 }
                          )}
                        </span>
                        <span className="text-sm text-gray-600">
                          (incl. GST)
                        </span>
                      </div>

                      {/* Price Breakdown */}
                      <div className="space-y-2 pt-4 border-t border-primary-200">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Metal Value:</span>
                          <span className="font-medium text-gray-900">
                            ₹
                            {primaryPricing.data.breakdown?.metalValue?.toLocaleString(
                              "en-IN",
                              { maximumFractionDigits: 0 }
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Making Charges:</span>
                          <span className="font-medium text-gray-900">
                            ₹
                            {primaryPricing.data.breakdown?.makingCharges?.toLocaleString(
                              "en-IN",
                              { maximumFractionDigits: 0 }
                            )}
                          </span>
                        </div>
                        {primaryPricing.data.breakdown?.wastageCharges > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">Wastage:</span>
                            <span className="font-medium text-gray-900">
                              ₹
                              {primaryPricing.data.breakdown?.wastageCharges?.toLocaleString(
                                "en-IN",
                                { maximumFractionDigits: 0 }
                              )}
                            </span>
                          </div>
                        )}
                        {primaryPricing.data.breakdown?.stoneCharges > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">
                              Stone Charges:
                            </span>
                            <span className="font-medium text-gray-900">
                              ₹
                              {primaryPricing.data.breakdown?.stoneCharges?.toLocaleString(
                                "en-IN",
                                { maximumFractionDigits: 0 }
                              )}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">
                            GST ({primaryPricing.data.breakdown?.gst?.rate}%):
                          </span>
                          <span className="font-medium text-gray-900">
                            ₹
                            {primaryPricing.data.breakdown?.gst?.amount?.toLocaleString(
                              "en-IN",
                              { maximumFractionDigits: 0 }
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-primary-200 text-base">
                          <span className="font-semibold text-gray-900">
                            Total:
                          </span>
                          <span className="font-bold text-primary">
                            ₹
                            {primaryPricing.data.breakdown?.finalAmount?.toLocaleString(
                              "en-IN",
                              { maximumFractionDigits: 0 }
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Metal Rates */}
                      <div className="mt-4 pt-4 border-t border-primary-200">
                        <p className="text-xs text-primary-700 mb-2">
                          Current Metal Rates
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-600">Sell Rate:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              ₹
                              {primaryPricing.data.rates?.sell?.toLocaleString()}
                              /g
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Buy Rate:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              ₹
                              {primaryPricing.data.rates?.buy?.toLocaleString()}
                              /g
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tabs */}
                  <div>
                    <div className="flex border-b border-gray-200">
                      {adminView && (
                        <button
                          onClick={() => setActiveTab("details")}
                          className={`px-4 py-2 font-medium text-sm transition-colors ${
                            activeTab === "details"
                              ? "text-primary border-b-2 border-primary"
                              : "text-gray-600 hover:text-gray-900"
                          }`}
                        >
                          Details
                        </button>
                      )}
                      <button
                        onClick={() => setActiveTab("specifications")}
                        className={`px-4 py-2 font-medium text-sm transition-colors ${
                          activeTab === "specifications"
                            ? "text-primary border-b-2 border-primary"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Specifications
                      </button>
                      {adminView && (
                        <button
                          onClick={() => setActiveTab("availability")}
                          className={`px-4 py-2 font-medium text-sm transition-colors ${
                            activeTab === "availability"
                              ? "text-primary border-b-2 border-primary"
                              : "text-gray-600 hover:text-gray-900"
                          }`}
                        >
                          Availability
                        </button>
                      )}
                    </div>

                    <div className="mt-4">
                      {/* Details Tab */}
                      {adminView && activeTab === "details" && (
                        <div className="space-y-4">
                          {/* Description */}
                          {product.description && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                Description
                              </h4>
                              <p className="text-sm text-gray-600">
                                {product.description}
                              </p>
                            </div>
                          )}

                          {/* Categories */}
                          {product.categories?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                Categories
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {product.categories.map((category) => (
                                  <span
                                    key={category._id}
                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                                  >
                                    {category.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Tags */}
                          {product.tags?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                Tags
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {product.tags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Created By */}
                          {product.createdBy && (
                            <div className="bg-gray-50 rounded-lg p-3">
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                Product Information
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <UserIcon className="w-4 h-4 text-gray-400" />
                                  <span className="text-gray-600">
                                    Created by:
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    {product.createdBy.adminName} (
                                    {product.createdBy.role})
                                  </span>
                                </div>
                                {product.lastModifiedBy && (
                                  <div className="flex items-center gap-2">
                                    <UserIcon className="w-4 h-4 text-gray-400" />
                                    <span className="text-gray-600">
                                      Last modified by:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {product.lastModifiedBy.adminName}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Specifications Tab */}
                      {activeTab === "specifications" && (
                        <div className="space-y-4">
                          {/* Weight Details */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Weight Details
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <ScaleIcon className="w-4 h-4 text-gray-500" />
                                  <span className="text-xs text-gray-600">
                                    Gross Weight
                                  </span>
                                </div>
                                <p className="text-lg font-semibold text-gray-900">
                                  {product.grossWeight}g
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <ScaleIcon className="w-4 h-4 text-gray-500" />
                                  <span className="text-xs text-gray-600">
                                    Net Weight
                                  </span>
                                </div>
                                <p className="text-lg font-semibold text-gray-900">
                                  {product.netWeight}g
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Metal Details */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Metal Details
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <SparklesIcon className="w-4 h-4 text-gray-500" />
                                  <span className="text-xs text-gray-600">
                                    Purity
                                  </span>
                                </div>
                                <p className="text-lg font-semibold text-gray-900">
                                  {product.metalDetails?.purity ||
                                    product.purity}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <TagIcon className="w-4 h-4 text-gray-500" />
                                  <span className="text-xs text-gray-600">
                                    Metal Color
                                  </span>
                                </div>
                                <p className="text-lg font-semibold text-gray-900">
                                  {product.metalDetails?.color || "Yellow"}
                                </p>
                              </div>
                              {product.metalDetails?.karat && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <span className="text-xs text-gray-600">
                                    Karat
                                  </span>
                                  <p className="text-lg font-semibold text-gray-900">
                                    {product.metalDetails.karat}K
                                  </p>
                                </div>
                              )}
                              {product.metalDetails?.fineness && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <span className="text-xs text-gray-600">
                                    Fineness
                                  </span>
                                  <p className="text-lg font-semibold text-gray-900">
                                    {product.metalDetails.fineness}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Hallmark Details */}
                          {product.hallmark?.isHallmarked && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <ShieldCheckIcon className="w-4 h-4 text-yellow-600" />
                                Hallmark Details
                              </h4>
                              <div className="bg-yellow-50 rounded-lg p-4 space-y-2">
                                {product.hallmark.huid && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">HUID:</span>
                                    <span className="font-medium text-gray-900">
                                      {product.hallmark.huid}
                                    </span>
                                  </div>
                                )}
                                {product.hallmark.centerCode && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Center Code:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {product.hallmark.centerCode}
                                    </span>
                                  </div>
                                )}
                                {product.hallmark.year && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Year:</span>
                                    <span className="font-medium text-gray-900">
                                      {product.hallmark.year}
                                    </span>
                                  </div>
                                )}
                                {product.hallmark.jewellerCode && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Jeweller Code:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {product.hallmark.jewellerCode}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Manufacturing Details */}
                          {adminView && product.manufacturing?.karigarCode && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <OfficeBuildingIcon className="w-4 h-4 text-gray-600" />
                                Manufacturing Details
                              </h4>
                              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    Karigar Code:
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    {product.manufacturing.karigarCode}
                                  </span>
                                </div>
                                {product.manufacturing.craftType && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Craft Type:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {product.manufacturing.craftType}
                                    </span>
                                  </div>
                                )}
                                {product.manufacturing.technique && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Technique:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {product.manufacturing.technique}
                                    </span>
                                  </div>
                                )}
                                {product.manufacturing.origin && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Origin:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {product.manufacturing.origin}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Stone Details */}
                          {product.hasStones && product.stones?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                Stone Details
                              </h4>
                              <div className="space-y-2">
                                {product.stones.map((stone, index) => (
                                  <div
                                    key={index}
                                    className="bg-purple-50 rounded-lg p-3"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <p className="font-medium text-gray-900">
                                          {stone.type}
                                        </p>
                                        {stone.shape && (
                                          <p className="text-xs text-gray-600">
                                            Shape: {stone.shape}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm font-semibold text-gray-900">
                                          {stone.weight} ct
                                        </p>
                                        {stone.count && (
                                          <p className="text-xs text-gray-600">
                                            Count: {stone.count}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Availability Tab */}
                      {adminView && activeTab === "availability" && (
                        <div className="space-y-4">
                          {/* Stock Summary */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Stock Summary
                            </h4>
                            <div className="grid grid-cols-3 gap-3 text-sm">
                              <div>
                                <p className="text-gray-600">Total</p>
                                <p className="text-lg font-bold text-gray-900">
                                  {product.stock?.totalQuantity || 0}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Reserved</p>
                                <p className="text-lg font-bold text-orange-600">
                                  {product.stock?.reservedQuantity || 0}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Available</p>
                                <p className="text-lg font-bold text-green-600">
                                  {product.stock?.availableQuantity || 0}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Location Stock */}
                          {product.stock?.locationStock?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                Stock by Location
                              </h4>
                              <div className="space-y-2">
                                {product.stock.locationStock.map(
                                  (location, index) => (
                                    <div
                                      key={index}
                                      className="flex justify-between items-center bg-white border border-gray-200 rounded-lg p-3 hover:border-primary transition-colors"
                                    >
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <LocationMarkerIcon className="w-4 h-4 text-gray-400" />
                                          <p className="font-medium text-gray-900">
                                            {location.branchCode}
                                          </p>
                                        </div>
                                        {location.storeCode && (
                                          <p className="text-xs text-gray-500 ml-6">
                                            Store: {location.storeCode}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <p className="font-semibold text-gray-900">
                                          {location.available} available
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {location.reserved} reserved of{" "}
                                          {location.quantity} total
                                        </p>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                          {/* Branch Pricing */}
                          {allPricingBranches.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                Pricing by Branch
                              </h4>
                              <div className="space-y-2">
                                {allPricingBranches.map(
                                  ({ branch, data }, index) => (
                                    <div
                                      key={index}
                                      className="bg-white border border-gray-200 rounded-lg p-3"
                                    >
                                      <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center gap-2">
                                          <LocationMarkerIcon className="w-4 h-4 text-primary" />
                                          <p className="font-medium text-gray-900">
                                            {branch}
                                          </p>
                                        </div>
                                        <p className="text-lg font-bold text-primary">
                                          ₹
                                          {data.breakdown?.finalAmount?.toLocaleString(
                                            "en-IN",
                                            { maximumFractionDigits: 0 }
                                          )}
                                        </p>
                                      </div>
                                      <div className="text-xs text-gray-600 space-y-1">
                                        <div className="flex justify-between">
                                          <span>Stock Available:</span>
                                          <span className="font-medium">
                                            {data.stock?.available || 0}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Sell Rate:</span>
                                          <span className="font-medium">
                                            ₹
                                            {data.rates?.sell?.toLocaleString()}
                                            /g
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Media Lightbox */}
      <MediaLightbox
        media={media}
        initialIndex={selectedMediaIndex}
        isOpen={showLightbox}
        onClose={() => setShowLightbox(false)}
        productName={product.name}
      />
    </>
  );
};

export default ProductDetailsModal;
